// ─────────────────────────────────────────────────────────────────────────────
// SubscriptionBanners
//
// Top-of-Dashboard alerts that surface subscription health states the user
// needs to act on. Two banners, mutually exclusive (you can't be past_due
// AND cancellation-pending at the same time per Stripe state semantics):
//
//   - CancellationPendingBanner (yellow, dismissible per cancellation date)
//   - PastDueBanner (red, NOT dismissible — too urgent)
//
// The default export SubscriptionBanners picks which one (if any) to render
// based on profile state. Dashboard just imports and drops <SubscriptionBanners
// profile={profile} /> at the top.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { C } from "../../lib/colors";

// localStorage key for the cancellation banner. Keyed to the cancellation
// date so that if a user cancels → dismisses → un-cancels → cancels again
// with a new date, the banner reappears (the key is different).
const CANCEL_BANNER_DISMISS_KEY = "fos_dismissed_cancel_banner";

// ─── Banner dispatcher ───────────────────────────────────────────────────────
//
// Decides which banner (if any) to render. Used by Dashboard at the top.

export function SubscriptionBanners({ profile }) {
  // Gifted accounts shouldn't see billing-related alarms.
  if (profile?.is_gifted) return null;

  const isCancellationPending =
    profile?.cancel_at_period_end === true &&
    (profile?.subscription_status === "active" ||
     profile?.subscription_status === "trialing");

  const isPastDue = profile?.subscription_status === "past_due";

  if (isPastDue) {
    return <PastDueBanner profile={profile} />;
  }
  if (isCancellationPending) {
    return <CancellationPendingBanner profile={profile} />;
  }
  return null;
}

// ─── Cancellation pending (yellow, dismissible) ──────────────────────────────

function CancellationPendingBanner({ profile }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Dismissal is keyed to the cancellation date. If the user dismisses, then
  // a NEW cancellation appears later with a different end date, the banner
  // shows again.
  const cancelDate = profile?.subscription_end || "";

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CANCEL_BANNER_DISMISS_KEY);
      if (stored === cancelDate) setDismissed(true);
    } catch {}
  }, [cancelDate]);

  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(CANCEL_BANNER_DISMISS_KEY, cancelDate);
    } catch {}
    setDismissed(true);
  };

  const formattedDate = cancelDate
    ? new Date(cancelDate).toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
      })
    : "the scheduled date";

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "12px 16px",
      background: C.yellow + "12",
      border: `1px solid ${C.yellow}40`,
      borderLeft: `3px solid ${C.yellow}`,
      borderRadius: 8,
      flexWrap: "wrap",
    }}>
      <div style={{ fontSize: 18, color: C.yellow, lineHeight: 1, flexShrink: 0 }}>⚠</div>

      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.yellow }}>
          Your subscription cancels on {formattedDate}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          You'll keep full access until then. Change your mind any time in Billing.
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button
          onClick={() => navigate("/billing")}
          style={{
            padding: "7px 14px",
            background: C.yellow,
            color: "#1a1d2e",
            border: "none",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Manage in Billing →
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          title="Dismiss"
          style={{
            padding: "6px 10px",
            background: "transparent",
            border: `1px solid ${C.yellow}40`,
            borderRadius: 6,
            color: C.yellow,
            fontSize: 13,
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ─── Past due (red, NOT dismissible) ─────────────────────────────────────────
//
// Stripe is retrying the card (up to 8 retries over 2 weeks per our config).
// The user has full access during that window — this banner is the in-app
// reminder. NOT dismissible because:
//   - The condition is urgent (lose access if retries fail)
//   - Stripe's emails may go to spam
//   - Once resolved, the banner disappears on its own (status flips back
//     to active via the webhook)

function PastDueBanner({ profile }) {
  const navigate = useNavigate();

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "12px 16px",
      background: C.red + "12",
      border: `1px solid ${C.red}50`,
      borderLeft: `3px solid ${C.red}`,
      borderRadius: 8,
      flexWrap: "wrap",
    }}>
      <div style={{ fontSize: 18, color: C.red, lineHeight: 1, flexShrink: 0 }}>⚠</div>

      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.red }}>
          Your last payment failed
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          Stripe is retrying automatically. Update your payment method to
          avoid losing access — you have up to 2 weeks before the
          subscription cancels.
        </div>
      </div>

      <button
        onClick={() => navigate("/billing")}
        style={{
          padding: "7px 14px",
          background: C.red,
          color: "#fff",
          border: "none",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        Update payment →
      </button>
    </div>
  );
}
